import { useMemo } from "react";
import { slugify } from "./markdown";

interface Heading {
  level: 2 | 3;
  text: string;
  id: string;
}

// Strip common inline markdown wrappers so the TOC label matches the
// rendered heading. Bugbot flagged that `alert_burst_rejected` on a
// troubleshooting heading appeared with backticks in the right rail
// but plain text in the article body.
function stripInlineMarkdown(text: string): string {
  return text
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/\*\*([^*]+)\*\*/g, "$1") // bold
    .replace(/\*([^*]+)\*/g, "$1") // italic
    .replace(/_([^_]+)_/g, "$1") // italic with underscore
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1"); // link → link text
}

function extractHeadings(markdown: string): Heading[] {
  const headings: Heading[] = [];
  const lines = markdown.split("\n");
  let inCodeFence = false;
  for (const line of lines) {
    if (line.startsWith("```")) {
      inCodeFence = !inCodeFence;
      continue;
    }
    if (inCodeFence) continue;
    const h2 = /^##\s+(.+?)\s*$/.exec(line);
    if (h2) {
      const text = stripInlineMarkdown(h2[1]);
      headings.push({ level: 2, text, id: slugify(text) });
      continue;
    }
    const h3 = /^###\s+(.+?)\s*$/.exec(line);
    if (h3) {
      const text = stripInlineMarkdown(h3[1]);
      headings.push({ level: 3, text, id: slugify(text) });
    }
  }
  return headings;
}

export function TableOfContents({ markdown }: { markdown: string }) {
  const headings = useMemo(() => extractHeadings(markdown), [markdown]);

  if (headings.length < 3) return null;

  return (
    <nav className="w-48 flex-shrink-0 sticky top-4 self-start py-4 pl-4 border-l border-border">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70 mb-2">
        On this page
      </p>
      <ul className="space-y-1">
        {headings.map((h) => (
          <li key={h.id} className={h.level === 3 ? "ml-3" : ""}>
            <a
              href={`#${h.id}`}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
